import { StrictMode } from 'react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

const getCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  const value = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!value) return null;
  try {
    return decodeURIComponent(value.slice(prefix.length));
  } catch {
    return null;
  }
};

// `/` is a static landing-page rewrite, so it does not run the Next.js app's
// normal session loader. Verify the same cookie against the API before moving
// a signed-in visitor into the product. Invalid/expired cookies simply leave
// the visitor on the public landing page.
const redirectAuthenticatedVisitor = async () => {
  if (window.location.pathname !== '/') return;
  const token = getCookie('token');
  if (!token) return;

  try {
    const response = await fetch('/api/users/me', {
      headers: { Authorization: token },
      credentials: 'same-origin'
    });
    if (!response.ok) return;

    const user = (await response.json())?.data;
    if (!user?._id) return;
    const destination = user.isPerformer
      ? `/${encodeURIComponent(user.username || user._id)}`
      : '/home';
    window.location.replace(destination);
  } catch {
    // A temporary network failure must not hide the landing page.
  }
};

void redirectAuthenticatedVisitor();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Register service worker so Chrome treats the landing as PWA-installable.
// Without an active SW + manifest link, beforeinstallprompt never fires.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  const register = () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // ignore registration errors silently
    })
  }
  if (document.readyState === 'complete') {
    register()
  } else {
    window.addEventListener('load', register)
  }
}
